import React from 'react';
import { vocabularyLevelChecker } from '@/lib/vocabularyLevel';

interface PracticeWordCardProps {
  word: string;
  index: number | string;
  chunkIndex?: number;
  hiddenWords: Record<string, boolean>;
  setHiddenWords: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isWordCheckerReady?: boolean;
  vocabularyTerms?: string[];
  practiceModeType?: 'vocabulary' | 'toefl';
}

export function PracticeWordCard({
  word,
  index,
  chunkIndex = -1,
  hiddenWords,
  setHiddenWords,
  isWordCheckerReady = false,
  vocabularyTerms = [],
  practiceModeType = 'toefl'
}: PracticeWordCardProps) {
  
  // 检测逻辑：支持两种模式
  const shouldRenderHidden = React.useCallback(() => {
    // Check for contractions
    const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
    if (isContraction) return false;

    // Check if it's a valid English word
    const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
    if (!isValidWord) return false;
    
    // Key Words模式：检查预设词汇列表（支持短语和单词）
    if (practiceModeType === 'vocabulary' && vocabularyTerms.length > 0) {
      const wordLower = word.toLowerCase();
      
      // 检查完全匹配的单词
      const exactMatch = vocabularyTerms.some(term => term.toLowerCase() === wordLower);
      
      // 检查单词是否为某个短语的一部分
      const partOfPhrase = vocabularyTerms.some(term => {
        const termLower = term.toLowerCase();
        // 检查term是否包含这个单词（作为完整单词，不是子字符串）
        const wordRegex = new RegExp(`\\b${wordLower}\\b`, 'i');
        return wordRegex.test(termLower);
      });
      
      const found = exactMatch || partOfPhrase;
      
      
      return found;
    }
    
    // TOEFL模式：使用词汇检查器
    if (practiceModeType === 'toefl' && isWordCheckerReady) {
      try {
        const hasTranslation = vocabularyLevelChecker.hasTranslation(word);
        return hasTranslation;
      } catch (error) {
        console.error("Error checking vocabulary translation:", error);
        return false;
      }
    }
    
    return false;
  }, [word, practiceModeType, vocabularyTerms, isWordCheckerReady]);

  // 这是一个词汇单词，处理隐藏逻辑
  // 使用包含 chunkIndex 的 key，与 handleVocabWordCompleted 保持一致
  const uniqueKey = chunkIndex >= 0 ? `${word.toLowerCase()}-${chunkIndex}` : word.toLowerCase();
  const shouldHide = shouldRenderHidden();
  
  // 使用 useEffect 来初始化隐藏状态，避免在渲染期间直接调用 setState
  React.useEffect(() => {
    // 只有在应该隐藏且还没有状态记录时才初始化
    if (shouldHide && !(uniqueKey in hiddenWords)) {
      setHiddenWords(prev => ({
        ...prev,
        [uniqueKey]: false // false = 隐藏
      }));
    }
  }, [uniqueKey, hiddenWords, setHiddenWords, shouldHide]);

  if (!shouldHide) {
    return <span key={index}>{word}</span>;
  }
  
  const isRevealed = hiddenWords[uniqueKey] === true;

  // 只在开发环境且词汇状态改变时打印日志
  if (process.env.NODE_ENV === 'development' && hiddenWords[uniqueKey] !== undefined) {
    // console.log(`🔍 PracticeWordCard "${word}" state:`, { uniqueKey, isRevealed });
  }

  if (isRevealed) {
    // 显示已reveal的词汇
    return (
      <span 
        key={index}
        className="font-medium text-[#d97757]"
      >
        {word}
      </span>
    );
  } else {
    // 显示隐藏的词汇（用下划线）
    return (
      <span 
        key={index}
        className="cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // console.log(`🎯 Revealing word: ${word}, key: ${uniqueKey}`);
          setHiddenWords(prev => ({
            ...prev,
            [uniqueKey]: true // 点击后设置为 true（显示）
          }));
        }}
      >
        {word.split('').map((char, charIndex) => (
          <span key={charIndex}>
            {/\s/.test(char) ? (
              char
            ) : (
              <span 
                className="border-b border-solid border-[#d97757] mx-[1px] inline-block" 
                style={{ width: '0.5em', height: '0.5px' }}
              />
            )}
          </span>
        ))}
      </span>
    );
  }
}